import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { devtools } from "zustand/middleware"
import { AddressOption } from "@/modules/care-provider/advanced-search/care-provider-address-filter/care-provider-address-select"

interface CareProviderManualAddressStoreActions {
  setAddresses: (address: AddressOption) => void
  clearStore: VoidFunction
}

interface ICareProviderManualAddressStore {
  addresses: AddressOption[] | null
}

const initialState: ICareProviderManualAddressStore = {
  addresses: null,
}

const createCareProviderManualAddressStore = () =>
  create<ICareProviderManualAddressStore & CareProviderManualAddressStoreActions>()(
    devtools(
      (set) => ({
        ...initialState,
        setAddresses: (newAddress: AddressOption) =>
          set((state) => ({
            addresses: [...(state.addresses ?? []), newAddress],
          })),
        clearStore: () => set(initialState),
      }),
      { name: "careProviderManualAddressStore" },
    ),
  )

type CareProviderManualAddressStoreType = ReturnType<typeof createCareProviderManualAddressStore>

const CareProviderManualAddressStoreContext = createContext<CareProviderManualAddressStoreType | null>(null)

type CareProviderManualAddressStoreProviderProps = React.PropsWithChildren

export const CareProviderManualAddressStoreProvider = ({ children }: CareProviderManualAddressStoreProviderProps) => {
  const storeRef = useRef<CareProviderManualAddressStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createCareProviderManualAddressStore()
  }
  return (
    <CareProviderManualAddressStoreContext.Provider value={storeRef.current}>
      {children}
    </CareProviderManualAddressStoreContext.Provider>
  )
}

export function useCareProviderManualAddressStoreContext<T>(
  selector: (state: ICareProviderManualAddressStore & CareProviderManualAddressStoreActions) => T,
): T {
  const store = useContext(CareProviderManualAddressStoreContext)
  if (!store) throw new Error("Missing CareProviderManualAddressStoreProvider in the tree")
  return useStore(store, selector)
}
